"use client";

import { Link } from "@/i18n/navigation";
import { absoluteLocalizedUrl } from "@/lib/i18n/pageMetadata";
import {
  PhoneIcon,
  ClipboardDocumentCheckIcon,
  HomeModernIcon,
  BuildingOffice2Icon,
  ArchiveBoxIcon,
  GiftIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import CtaLink from "@/components/ui/CtaLink";
import Contact from "@/components/Contact/Contact";
import ContentImage from "@/components/media/ContentImage";

const PHONE_TEL = "+32493966507";
const PHONE_DISPLAY = "+32 (0)493 96 65 07";

// imageId pairs aren't translatable, kept separate from CONTENT.
const BEFORE_AFTER = [
  { before: "realisations-avant-1", after: "realisations-apres-1" },
  { before: "realisations-avant-2", after: "realisations-apres-2" },
  { before: "realisations-avant-3", after: "realisations-apres-3" },
  { before: "realisations-avant-4", after: "realisations-apres-4" },
];

// Icons aren't translatable either, zipped back onto CONTENT[locale].types by
// array index (same pattern as homepage PageClient.jsx).
const TYPE_ICONS = [HomeModernIcon, BuildingOffice2Icon, ArchiveBoxIcon, GiftIcon];
const TYPE_HREFS = ["/debarras-maison", "/debarras-appartement", "/debarras-cave-grenier-garage", "/rachat-objets"];

const BREADCRUMB_HOME = { fr: "Accueil", nl: "Home", en: "Home" };
const BREADCRUMB_REALISATIONS = { fr: "Réalisations", nl: "Realisaties", en: "Our work" };

const CONTENT = {
  fr: {
    h1: "Réalisations de vide maison à Bruxelles",
    heroText:
      "Chaque photo ci-dessous correspond à un chantier réellement mené par notre équipe à Bruxelles : une pièce encombrée, puis la même pièce vidée et nettoyée. Pas de mise en scène ni de visuels d'agence — uniquement nos propres interventions. Notre activité dans la capitale est encore jeune, et cette page s'enrichit chantier après chantier. Elle donne malgré tout une idée fidèle de notre façon de travailler et du résultat que vous pouvez attendre, qu'il s'agisse d'une maison, d'un appartement ou d'une cave à vider.",
    ctaQuote: "Demander un devis gratuit",
    avantApres: {
      title: "Avant / après",
      subtitle: "Des photos brutes, prises avant et après l'intervention, sans retouche destinée à embellir le résultat.",
      avant: "Avant",
      apres: "Après",
      chantier: "Chantier",
    },
    comment: {
      title: "Comment se déroule une intervention, du premier contact à l'évacuation",
      p1: "Tout commence par un premier contact — appel ou formulaire — où vous décrivez le bien à vider et, si possible, envoyez quelques photos. Selon la complexité de la situation, cela suffit à établir une estimation, ou une visite sur place est proposée pour affiner le devis.",
      p2: "Le jour de l'intervention, notre équipe trie sur place : ce qui peut être racheté ou valorisé est mis de côté et estimé, le reste est évacué vers les filières adaptées (recyclage, réemploi, déchet ultime). À la fin du chantier, les pièces sont vidées et sommairement nettoyées, prêtes à être visitées, vendues ou remises en location.",
      p3Before: "Un chantier se prépare aussi un peu de votre côté : indiquer les meubles ou objets à conserver et l'accès prévu au bien facilite l'organisation. Notre ",
      p3Link: "checklist avant vide maison",
      p3After: " détaille ces quelques points à vérifier avant notre venue.",
    },
    typesTitle: "Les types de biens que nous prenons en charge à Bruxelles",
    types: [
      { title: "Maisons unifamiliales et de maître", text: "Débarras complet du grenier à la cave, jardin et garage compris. Adapté aux successions comme aux ventes.", linkLabel: "Débarras de maison" },
      { title: "Appartements et kots", text: "Studios, appartements en étage, immeubles avec syndic — nous nous adaptons aux contraintes des parties communes.", linkLabel: "Débarras d'appartement" },
      { title: "Caves, greniers et garages", text: "Espaces annexes encombrés depuis des années, souvent riches en objets oubliés à redécouvrir.", linkLabel: "Débarras cave, grenier, garage" },
      { title: "Successions avec objets de valeur", text: "Estimation et rachat de mobilier ancien, bijoux, argenterie ou collections trouvés lors du débarras.", linkLabel: "Rachat d'objets" },
    ],
    typesOutro:
      "Ces quatre catégories reviennent le plus souvent dans les demandes que nous recevons à Bruxelles, mais chaque bien garde ses propres contraintes : une maison de maître à plusieurs étages ne se vide pas comme un appartement desservi par un ascenseur limité, ni comme une cave accessible uniquement par un escalier étroit. C'est pour cette raison que nous évaluons chaque situation avant de proposer un devis.",
    zonesLink: "Voir nos zones d'intervention à Bruxelles",
    varies: {
      title: "Ce qui varie d'un chantier à l'autre",
      p1: "Deux chantiers de vide maison ne se ressemblent jamais tout à fait, même à quelques rues d'écart. C'est pourquoi nous ne communiquons pas de prix au m³ fixe sur cette page : chaque situation est évaluée individuellement.",
      items: [
        "Le volume réel à évacuer",
        "L'accès au bien : étage, ascenseur, stationnement à Bruxelles",
        "La présence ou non d'objets valorisables",
        "Le délai souhaité (vente, succession, déménagement)",
      ],
      p2Before: "Le détail des critères qui influencent une estimation est expliqué sur notre page ",
      p2Link1: "tarifs",
      p2Mid: ". Pour vous faire un avis avant de nous contacter, notre page ",
      p2Link2: "avis",
      p2After: " explique aussi notre engagement qualité au quotidien.",
    },
    faqTitle: "Questions fréquentes sur nos réalisations à Bruxelles",
    faq: [
      { q: "Pourquoi n'y a-t-il que quelques exemples de chantiers sur cette page ?", a: "Parce que nous préférons montrer uniquement des interventions réelles plutôt que des visuels génériques. Notre activité à Bruxelles est récente : chaque nouveau chantier documenté vient s'ajouter progressivement à cette page. Le nombre d'exemples visibles aujourd'hui reflète simplement le stade actuel de notre développement dans la région, pas notre capacité à intervenir." },
      { q: "Les photos avant/après sont-elles retouchées ?", a: "Non. Ce sont des photos prises avant et après nos interventions, sans mise en scène ni retouche destinée à exagérer le résultat. La pièce est vidée et sommairement nettoyée, mais pas repeinte ni rénovée : notre mission s'arrête à l'évacuation et au nettoyage de base, pas aux travaux de rénovation." },
      { q: "Quel type de biens débarrassez-vous le plus souvent à Bruxelles ?", a: "Principalement des maisons unifamiliales dans le cadre de successions ou de ventes, des appartements lors d'un déménagement ou d'un décès, ainsi que des caves, greniers et garages encombrés depuis plusieurs années. Chaque configuration a ses propres contraintes d'accès et de volume, ce qui influence l'organisation du chantier." },
      { q: "Comment se passe le jour de l'intervention ?", a: "Notre équipe trie sur place : ce qui peut être racheté ou valorisé est mis de côté, le reste est évacué vers les filières adaptées. À la fin de l'intervention, les pièces sont vidées et sommairement nettoyées. La durée dépend directement du volume à traiter et de l'accès au bien (étage, ascenseur, stationnement)." },
      { q: "Le prix est-il le même pour chaque chantier photographié ?", a: "Non, et nous ne l'affichons volontairement pas sur cette page : le prix dépend du volume réel, de l'accès et de la présence d'objets valorisables. Les critères qui font varier une estimation sont détaillés sur notre page tarifs, avec un devis gratuit et personnalisé pour votre situation." },
      { q: "Que faites-vous des objets de valeur trouvés pendant un débarras ?", a: "Avant évacuation, nous identifions les meubles anciens, bijoux, argenterie ou objets de collection susceptibles d'être rachetés. Leur valeur est déduite du coût de l'intervention lorsque c'est possible. Le détail de cette démarche est expliqué sur notre page rachat d'objets." },
      { q: "Faut-il préparer quelque chose avant votre venue ?", a: "Ce n'est pas obligatoire, mais indiquer à l'avance les meubles ou objets à conserver, ainsi que l'accès prévu (parking, ascenseur, étage), permet d'organiser le chantier plus efficacement. Retrouvez nos conseils complets dans notre checklist avant vide maison." },
      { q: "Intervenez-vous dans toutes les communes de Bruxelles ?", a: "Oui, nous couvrons les 19 communes de la Région de Bruxelles-Capitale. Retrouvez le détail de nos zones d'intervention, commune par commune, sur notre page dédiée." },
    ],
    ctaFinal: {
      title: "Une situation à évaluer chez vous ?",
      text: "Décrivez-nous votre bien et vos contraintes d'accès, nous vous recontactons avec une estimation gratuite et sans engagement.",
    },
  },
  nl: {
    h1: "Realisaties van huis leegmaken in Brussel",
    heroText:
      "Elke foto hieronder komt overeen met een werf die daadwerkelijk door ons team in Brussel werd uitgevoerd: een volgestouwde kamer, en vervolgens diezelfde kamer leeggemaakt en gereinigd. Geen enscenering of stockbeelden — uitsluitend onze eigen interventies. Onze activiteit in de hoofdstad is nog jong, en deze pagina groeit werf na werf. Ze geeft niettemin een getrouw beeld van onze manier van werken en het resultaat dat u mag verwachten, of het nu gaat om een huis, een appartement of een kelder om te ledigen.",
    ctaQuote: "Gratis offerte aanvragen",
    avantApres: {
      title: "Voor / na",
      subtitle: "Ruwe foto's, genomen vóór en na de interventie, zonder retouche om het resultaat mooier voor te stellen.",
      avant: "Voor",
      apres: "Na",
      chantier: "Werf",
    },
    comment: {
      title: "Hoe verloopt een interventie, van het eerste contact tot de afvoer",
      p1: "Alles begint met een eerste contact — telefoontje of formulier — waarbij u het te ledigen pand beschrijft en, indien mogelijk, enkele foto's stuurt. Naargelang de complexiteit van de situatie volstaat dit om een schatting op te stellen, of wordt een bezoek ter plaatse voorgesteld om de offerte te verfijnen.",
      p2: "Op de dag van de interventie sorteert ons team ter plaatse: wat kan worden ingekocht of gevaloriseerd, wordt apart gezet en geschat, de rest wordt afgevoerd naar de geschikte kanalen (recyclage, hergebruik, restafval). Aan het einde van de werf zijn de kamers leeg en summier gereinigd, klaar om bezocht, verkocht of opnieuw verhuurd te worden.",
      p3Before: "Een werf wordt ook een beetje van uw kant voorbereid: aangeven welke meubelen of voorwerpen u wilt bewaren en welke toegang tot het pand voorzien is, vergemakkelijkt de organisatie. Onze ",
      p3Link: "checklist vóór het leegmaken van een huis",
      p3After: " behandelt deze enkele punten die u vóór ons bezoek moet controleren.",
    },
    typesTitle: "De types panden die wij in Brussel behandelen",
    types: [
      { title: "Eengezinswoningen en herenhuizen", text: "Volledige ontruiming van zolder tot kelder, tuin en garage inbegrepen. Geschikt voor zowel nalatenschappen als verkopen.", linkLabel: "Huis ontruimen" },
      { title: "Appartementen en kots", text: "Studio's, appartementen op verdieping, gebouwen met syndicus — wij passen ons aan de beperkingen van de gemeenschappelijke delen aan.", linkLabel: "Appartement ontruimen" },
      { title: "Kelders, zolders en garages", text: "Bijruimtes die al jaren vol staan, vaak rijk aan vergeten voorwerpen om te herontdekken.", linkLabel: "Kelder, zolder, garage ontruimen" },
      { title: "Nalatenschappen met waardevolle voorwerpen", text: "Schatting en inkoop van antiek meubilair, juwelen, zilverwerk of verzamelingen gevonden tijdens de ontruiming.", linkLabel: "Inkoop van voorwerpen" },
    ],
    typesOutro:
      "Deze vier categorieën komen het vaakst voor in de aanvragen die wij in Brussel ontvangen, maar elk pand behoudt zijn eigen beperkingen: een herenhuis met meerdere verdiepingen wordt niet leeggemaakt zoals een appartement met een beperkte lift, noch zoals een kelder die enkel bereikbaar is via een smalle trap. Daarom beoordelen wij elke situatie vóór wij een offerte voorstellen.",
    zonesLink: "Bekijk onze interventiezones in Brussel",
    varies: {
      title: "Wat van werf tot werf verschilt",
      p1: "Twee werven voor het leegmaken van een huis lijken nooit helemaal op elkaar, zelfs niet enkele straten verderop. Daarom communiceren wij op deze pagina geen vaste prijs per m³: elke situatie wordt individueel beoordeeld.",
      items: [
        "Het werkelijke volume om af te voeren",
        "De toegang tot het pand: verdieping, lift, parkeren in Brussel",
        "De al dan niet aanwezigheid van waardevolle voorwerpen",
        "De gewenste termijn (verkoop, nalatenschap, verhuizing)",
      ],
      p2Before: "Het detail van de criteria die een schatting beïnvloeden, wordt uitgelegd op onze pagina ",
      p2Link1: "tarieven",
      p2Mid: ". Om u vóór het contact een mening te vormen, legt onze pagina ",
      p2Link2: "beoordelingen",
      p2After: " ook ons dagelijkse kwaliteitsengagement uit.",
    },
    faqTitle: "Veelgestelde vragen over onze realisaties in Brussel",
    faq: [
      { q: "Waarom staan er maar enkele voorbeelden van werven op deze pagina?", a: "Omdat wij liever uitsluitend echte interventies tonen dan generieke beelden. Onze activiteit in Brussel is recent: elke nieuw gedocumenteerde werf wordt geleidelijk aan deze pagina toegevoegd. Het aantal voorbeelden dat vandaag zichtbaar is, weerspiegelt gewoon de huidige fase van onze ontwikkeling in de regio, niet onze capaciteit om tussen te komen." },
      { q: "Zijn de voor/na-foto's bewerkt?", a: "Nee. Het zijn foto's genomen vóór en na onze interventies, zonder enscenering of retouche bedoeld om het resultaat te overdrijven. De ruimte wordt geledigd en summier gereinigd, maar niet herschilderd of gerenoveerd: onze opdracht stopt bij de afvoer en de basisreiniging, niet bij renovatiewerken." },
      { q: "Welk type panden ontruimt u het vaakst in Brussel?", a: "Voornamelijk eengezinswoningen in het kader van nalatenschappen of verkopen, appartementen bij een verhuizing of overlijden, evenals kelders, zolders en garages die al jarenlang vol staan. Elke configuratie heeft zijn eigen beperkingen inzake toegang en volume, wat de organisatie van de werf beïnvloedt." },
      { q: "Hoe verloopt de dag van de interventie?", a: "Ons team sorteert ter plaatse: wat kan worden ingekocht of gevaloriseerd, wordt apart gezet, de rest wordt afgevoerd naar de geschikte kanalen. Aan het einde van de interventie zijn de kamers geledigd en summier gereinigd. De duur hangt rechtstreeks af van het te behandelen volume en de toegang tot het pand (verdieping, lift, parkeren)." },
      { q: "Is de prijs dezelfde voor elke gefotografeerde werf?", a: "Nee, en wij tonen deze bewust niet op deze pagina: de prijs hangt af van het werkelijke volume, de toegang en de aanwezigheid van waardevolle voorwerpen. De criteria die een schatting doen variëren, worden uitgelegd op onze pagina tarieven, met een gratis en persoonlijke offerte voor uw situatie." },
      { q: "Wat doet u met de waardevolle voorwerpen die tijdens een ontruiming worden gevonden?", a: "Vóór de afvoer identificeren wij antiek meubilair, juwelen, zilverwerk of verzamelobjecten die kunnen worden ingekocht. Hun waarde wordt afgetrokken van de kostprijs van de interventie wanneer mogelijk. Het detail van deze aanpak wordt uitgelegd op onze pagina inkoop van voorwerpen." },
      { q: "Moet ik iets voorbereiden vóór uw komst?", a: "Dat is niet verplicht, maar het vooraf aangeven van de meubelen of voorwerpen die u wilt bewaren, evenals de voorziene toegang (parking, lift, verdieping), maakt het mogelijk de werf efficiënter te organiseren. Bekijk al onze tips in onze checklist vóór het leegmaken van een huis." },
      { q: "Bent u actief in alle gemeenten van Brussel?", a: "Ja, wij bedienen de 19 gemeenten van het Brussels Hoofdstedelijk Gewest. Bekijk het detail van onze interventiezones, gemeente per gemeente, op onze speciale pagina." },
    ],
    ctaFinal: {
      title: "Een situatie bij u thuis te evalueren?",
      text: "Beschrijf ons uw pand en de toegangsbeperkingen, wij nemen opnieuw contact met u op met een gratis en vrijblijvende schatting.",
    },
  },
  en: {
    h1: "House clearance projects in Brussels",
    heroText:
      "Every photo below corresponds to a job genuinely carried out by our team in Brussels: a cluttered room, then that same room cleared and cleaned. No staging or stock photos — only our own interventions. Our activity in the capital is still young, and this page grows job after job. It nonetheless gives a faithful idea of how we work and the result you can expect, whether it's a house, an apartment or a cellar to clear.",
    ctaQuote: "Request a free quote",
    avantApres: {
      title: "Before / after",
      subtitle: "Unedited photos, taken before and after the intervention, with no retouching to embellish the result.",
      avant: "Before",
      apres: "After",
      chantier: "Job",
    },
    comment: {
      title: "How an intervention unfolds, from first contact to removal",
      p1: "It all starts with a first contact — a call or the form — where you describe the property to be cleared and, if possible, send a few photos. Depending on the complexity of the situation, this is enough to draw up an estimate, or an on-site visit is offered to refine the quote.",
      p2: "On the day of the intervention, our team sorts on site: anything that can be bought back or reused is set aside and appraised, the rest is removed to the appropriate channels (recycling, reuse, final waste). At the end of the job, the rooms are cleared and given a basic clean, ready to be viewed, sold or re-let.",
      p3Before: "A job is also partly prepared on your side: indicating the furniture or items to keep and the planned access to the property makes organisation easier. Our ",
      p3Link: "checklist before a house clearance",
      p3After: " details these few points to check before our visit.",
    },
    typesTitle: "The types of properties we handle in Brussels",
    types: [
      { title: "Single-family homes and townhouses", text: "Full clearance from attic to cellar, garden and garage included. Suited to both estates and sales.", linkLabel: "House clearance" },
      { title: "Apartments and student rooms", text: "Studios, apartments on any floor, buildings with a property manager — we adapt to the constraints of shared areas.", linkLabel: "Apartment clearance" },
      { title: "Cellars, attics and garages", text: "Ancillary spaces cluttered for years, often rich in forgotten items waiting to be rediscovered.", linkLabel: "Cellar, attic, garage clearance" },
      { title: "Estates with valuable items", text: "Appraisal and buyback of antique furniture, jewellery, silverware or collections found during the clearance.", linkLabel: "Item buyback" },
    ],
    typesOutro:
      "These four categories come up most often in the requests we receive in Brussels, but every property keeps its own constraints: a multi-storey townhouse is not cleared the same way as an apartment served by a limited lift, nor like a cellar accessible only via a narrow staircase. That is why we assess each situation before proposing a quote.",
    zonesLink: "See our service areas in Brussels",
    varies: {
      title: "What varies from one job to another",
      p1: "No two house-clearance jobs are ever quite alike, even a few streets apart. That is why we do not publish a fixed price per m³ on this page: each situation is assessed individually.",
      items: [
        "The actual volume to be removed",
        "Access to the property: floor level, lift, parking in Brussels",
        "Whether or not valuable items are present",
        "The desired timeframe (sale, estate, move)",
      ],
      p2Before: "The details of the criteria that influence an estimate are explained on our ",
      p2Link1: "prices",
      p2Mid: " page. To form an opinion before contacting us, our ",
      p2Link2: "reviews",
      p2After: " page also explains our day-to-day quality commitment.",
    },
    faqTitle: "Frequently asked questions about our work in Brussels",
    faq: [
      { q: "Why are there only a few examples of jobs on this page?", a: "Because we prefer to show only genuine interventions rather than generic visuals. Our activity in Brussels is recent: every newly documented job is gradually added to this page. The number of examples visible today simply reflects the current stage of our development in the region, not our ability to take on work." },
      { q: "Are the before/after photos edited?", a: "No. These are photos taken before and after our interventions, with no staging or retouching intended to exaggerate the result. The room is cleared and given a basic clean, but not repainted or renovated: our job ends at removal and basic cleaning, not renovation work." },
      { q: "What type of properties do you clear most often in Brussels?", a: "Mainly single-family homes as part of estates or sales, apartments during a move or a bereavement, as well as cellars, attics and garages cluttered for several years. Each configuration has its own access and volume constraints, which affects how the job is organised." },
      { q: "How does the day of the intervention go?", a: "Our team sorts on site: anything that can be bought back or reused is set aside and appraised, the rest is removed to the appropriate channels. At the end of the intervention, the rooms are cleared and given a basic clean. The duration depends directly on the volume to be handled and access to the property (floor level, lift, parking)." },
      { q: "Is the price the same for every job photographed?", a: "No, and we deliberately do not display it on this page: the price depends on the actual volume, access and the presence of valuable items. The criteria that make an estimate vary are detailed on our prices page, with a free, personalised quote for your situation." },
      { q: "What do you do with valuable items found during a clearance?", a: "Before removal, we identify antique furniture, jewellery, silverware or collectible items that could be bought back. Their value is deducted from the cost of the intervention where possible. The details of this process are explained on our item buyback page." },
      { q: "Do I need to prepare anything before your visit?", a: "It is not compulsory, but indicating in advance which furniture or items to keep, as well as the planned access (parking, lift, floor level), helps organise the job more efficiently. Find our full advice in our checklist before a house clearance." },
      { q: "Do you operate in every commune of Brussels?", a: "Yes, we cover all 19 communes of the Brussels-Capital Region. See the details of our service areas, commune by commune, on our dedicated page." },
    ],
    ctaFinal: {
      title: "Have a situation at home to assess?",
      text: "Describe your property and access constraints to us, and we will get back to you with a free, no-obligation estimate.",
    },
  },
};

export default function PageClient({ locale }) {
  const c = CONTENT[locale];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: BREADCRUMB_HOME[locale], item: absoluteLocalizedUrl(locale, "") },
      { "@type": "ListItem", position: 2, name: BREADCRUMB_REALISATIONS[locale], item: absoluteLocalizedUrl(locale, "/realisations") },
    ],
  };

  return (
    <div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }} />

      <section className="relative overflow-hidden bg-sand text-ink">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: "radial-gradient(900px 480px at 85% -10%, rgba(63,108,81,0.08), transparent 55%)",
          }}
        />
        <div className="relative mx-auto max-w-[1100px] px-6 py-20 text-center lg:px-10 lg:py-28">
          <h1 className="font-serif text-4xl font-black md:text-5xl">{c.h1}</h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-ink/70 leading-relaxed">
            {c.heroText}
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.ctaQuote}
            </CtaLink>
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <p className="text-center font-serif text-3xl font-extrabold text-brand-900">{c.avantApres.title}</p>
          <p className="mx-auto mt-4 max-w-2xl text-center text-neutral-600">
            {c.avantApres.subtitle}
          </p>
          <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {BEFORE_AFTER.map((pair, index) => (
              <div key={pair.before} className="overflow-hidden rounded-3xl border border-sand-line">
                <div className="grid grid-cols-2">
                  <div className="relative aspect-[3/4]">
                    <ContentImage imageId={pair.before} className="h-full w-full object-cover" sizes="25vw" />
                    <span className="absolute left-2 top-2 rounded-full bg-ink/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                      {c.avantApres.avant}
                    </span>
                  </div>
                  <div className="relative aspect-[3/4]">
                    <ContentImage imageId={pair.after} className="h-full w-full object-cover" sizes="25vw" />
                    <span className="absolute left-2 top-2 rounded-full bg-cta-700 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                      {c.avantApres.apres}
                    </span>
                  </div>
                </div>
                <p className="p-3 text-center text-xs text-neutral-500">
                  {c.avantApres.chantier} {index + 1}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand py-16">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10 space-y-5">
          <h2 className="font-serif text-3xl font-extrabold text-brand-900">
            {c.comment.title}
          </h2>
          <p className="leading-relaxed text-neutral-700">
            {c.comment.p1}
          </p>
          <p className="leading-relaxed text-neutral-700">
            {c.comment.p2}
          </p>
          <p className="leading-relaxed text-neutral-700">
            {c.comment.p3Before}
            <Link href="/blog/checklist-avant-vide-maison" className="font-semibold text-accent underline underline-offset-4">
              {c.comment.p3Link}
            </Link>
            {c.comment.p3After}
          </p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-[1200px] px-6 lg:px-10">
          <h2 className="text-center font-serif text-3xl font-extrabold text-brand-900">
            {c.typesTitle}
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {c.types.map((type, i) => {
              const Icon = TYPE_ICONS[i];
              return (
                <div key={type.title} className="rounded-3xl border border-sand-line bg-sand p-7">
                  <Icon className="h-9 w-9 text-accent" />
                  <p className="mt-4 font-serif text-lg font-bold text-ink">{type.title}</p>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{type.text}</p>
                  <Link
                    href={TYPE_HREFS[i]}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-accent hover:text-accent-dark"
                  >
                    {type.linkLabel}
                    <ArrowRightIcon className="h-3.5 w-3.5" />
                  </Link>
                </div>
              );
            })}
          </div>

          <p className="mx-auto mt-10 max-w-2xl text-center leading-relaxed text-neutral-600">
            {c.typesOutro}
          </p>

          <div className="mt-10 text-center">
            <Link href="/vide-maison" className="inline-flex items-center gap-2 font-bold text-accent hover:text-accent-dark">
              {c.zonesLink}
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-sand py-16">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <h2 className="font-serif text-3xl font-extrabold text-brand-900">{c.varies.title}</h2>
          <p className="mt-4 leading-relaxed text-neutral-700">
            {c.varies.p1}
          </p>
          <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {c.varies.items.map((item) => (
              <li key={item} className="rounded-2xl border border-sand-line bg-white p-4 text-sm font-semibold text-ink">
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-6 leading-relaxed text-neutral-700">
            {c.varies.p2Before}
            <Link href="/tarifs" className="font-semibold text-accent underline underline-offset-4">
              {c.varies.p2Link1}
            </Link>
            {c.varies.p2Mid}
            <Link href="/avis" className="font-semibold text-accent underline underline-offset-4">
              {c.varies.p2Link2}
            </Link>
            {c.varies.p2After}
          </p>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-[900px] px-6 lg:px-10">
          <h2 className="font-serif text-3xl font-extrabold text-brand-900">
            {c.faqTitle}
          </h2>
          <div className="mt-8 space-y-4">
            {c.faq.map((item) => (
              <div key={item.q} className="rounded-2xl border border-sand-line bg-sand p-5">
                <p className="font-bold text-ink">{item.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand py-16">
        <div className="mx-auto max-w-[800px] px-6 text-center lg:px-10">
          <h2 className="font-serif text-2xl font-extrabold text-brand-900 md:text-3xl">
            {c.ctaFinal.title}
          </h2>
          <p className="mx-auto mt-4 max-w-xl leading-relaxed text-neutral-700">
            {c.ctaFinal.text}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <CtaLink href={`tel:${PHONE_TEL}`} variant="accent" icon={PhoneIcon}>
              {PHONE_DISPLAY}
            </CtaLink>
            <CtaLink href="#contactus" variant="sand" icon={ClipboardDocumentCheckIcon}>
              {c.ctaQuote}
            </CtaLink>
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
